import React, { useState } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Edit, Check, X, Download, Mail, Share2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const PreviewAndEdit = ({ autobiographyData, onFinalize, onRegenerate }) => {
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(autobiographyData.content);
  const [editedTitle, setEditedTitle] = useState(autobiographyData.title);
  const [emailAddress, setEmailAddress] = useState('');
  const [showEmailForm, setShowEmailForm] = useState(false);

  const handleSaveEdits = () => {
    // Update the autobiography with edited content
    const updatedData = {
      ...autobiographyData,
      title: editedTitle,
      content: editedContent,
      word_count: editedContent.split(/\s+/).length
    };
    
    setIsEditing(false);
    onFinalize(updatedData);
    
    toast({
      title: "Changes Saved!",
      description: "Your autobiography has been updated.",
    });
  };

  const handleCancelEdit = () => {
    setEditedContent(autobiographyData.content);
    setEditedTitle(autobiographyData.title);
    setIsEditing(false);
  };

  const handleSendEmail = async () => {
    if (!emailAddress) {
      toast({
        title: "Email Required",
        description: "Please enter an email address.",
        variant: "destructive"
      });
      return;
    }

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/autobiography/${autobiographyData.id}/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailAddress })
      });

      if (!response.ok) throw new Error('Failed to send email');

      toast({
        title: "Email Sent!",
        description: `Your autobiography has been sent to ${emailAddress}`,
      });
      
      setShowEmailForm(false);
      setEmailAddress('');
    } catch (error) {
      toast({
        title: "Email Failed",
        description: "Unable to send email. You can download instead.",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header with Actions */}
        <div className="glass-effect rounded-2xl p-6 mb-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">
                Preview Your Autobiography
              </h1>
              <p className="text-gray-600">
                Review your story and make any changes before finalizing
              </p>
            </div>
            
            <div className="flex gap-3">
              {!isEditing ? (
                <>
                  <Button
                    onClick={() => setIsEditing(true)}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Edit className="w-4 h-4" />
                    Edit
                  </Button>
                  <Button
                    onClick={() => onFinalize(autobiographyData)}
                    className="bg-gradient-to-r from-pink-500 to-purple-500 text-white flex items-center gap-2 border-0"
                  >
                    <Check className="w-4 h-4" />
                    Looks Great!
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={handleCancelEdit}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveEdits}
                    className="bg-gradient-to-r from-green-500 to-emerald-500 text-white flex items-center gap-2 border-0"
                  >
                    <Check className="w-4 h-4" />
                    Save Changes
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 text-center shadow-md">
            <p className="text-3xl font-bold text-purple-500">{autobiographyData.word_count}</p>
            <p className="text-sm text-gray-600">Words</p>
          </div>
          <div className="bg-white rounded-xl p-4 text-center shadow-md">
            <p className="text-3xl font-bold text-pink-500">{autobiographyData.chapters}</p>
            <p className="text-sm text-gray-600">Chapters</p>
          </div>
          <div className="bg-white rounded-xl p-4 text-center shadow-md">
            <p className="text-3xl font-bold text-blue-500">~{Math.ceil(autobiographyData.word_count / 250)}</p>
            <p className="text-sm text-gray-600">Pages</p>
          </div>
        </div>

        {/* Content Preview/Edit */}
        <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 mb-8">
          {isEditing ? (
            <div className="space-y-6">
              <div>
                <Label htmlFor="title" className="text-lg font-semibold mb-2 block">
                  Title
                </Label>
                <Input
                  id="title"
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  className="text-2xl font-bold"
                />
              </div>
              
              <div>
                <Label htmlFor="content" className="text-lg font-semibold mb-2 block">
                  Content
                </Label>
                <Textarea
                  id="content"
                  value={editedContent}
                  onChange={(e) => setEditedContent(e.target.value)}
                  className="min-h-[600px] font-serif text-base leading-relaxed"
                />
              </div>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12 pb-8 border-b-2 border-gray-200">
                <h2 className="text-4xl font-serif font-bold text-gray-800 mb-3">
                  {autobiographyData.title}
                </h2>
                <p className="text-xl text-gray-600 italic">
                  {autobiographyData.subtitle}
                </p>
              </div>

              <div className="prose prose-lg max-w-none">
                <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                  {autobiographyData.content.split('\n').map((line, index) => {
                    if (line.startsWith('###')) {
                      return (
                        <h3 key={index} className="text-2xl font-bold text-gray-800 mt-8 mb-4">
                          {line.replace('###', '').trim()}
                        </h3>
                      );
                    } else if (line.startsWith('##')) {
                      return (
                        <h2 key={index} className="text-3xl font-bold text-gray-800 mt-6 mb-3 text-center italic">
                          {line.replace('##', '').trim()}
                        </h2>
                      );
                    } else if (line.startsWith('#')) {
                      return (
                        <h1 key={index} className="text-4xl font-bold text-gray-800 mb-2 text-center">
                          {line.replace('#', '').trim()}
                        </h1>
                      );
                    } else if (line.trim() === '---') {
                      return <hr key={index} className="my-8 border-gray-300" />;
                    } else if (line.trim() !== '') {
                      return (
                        <p key={index} className="mb-4 text-justify">
                          {line}
                        </p>
                      );
                    }
                    return <br key={index} />;
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        {!isEditing && (
          <div className="grid md:grid-cols-3 gap-4">
            <Button
              onClick={() => setShowEmailForm(!showEmailForm)}
              variant="outline"
              className="py-6 text-lg flex items-center justify-center gap-2 rounded-xl"
            >
              <Mail className="w-5 h-5" />
              Email to Me
            </Button>
            
            <Button
              onClick={() => onRegenerate()}
              variant="outline"
              className="py-6 text-lg flex items-center justify-center gap-2 rounded-xl"
            >
              <Edit className="w-5 h-5" />
              Regenerate Story
            </Button>
            
            <Button
              className="bg-gradient-to-r from-pink-500 to-purple-500 text-white py-6 text-lg flex items-center justify-center gap-2 rounded-xl border-0"
              onClick={() => onFinalize(autobiographyData)}
            >
              <Download className="w-5 h-5" />
              Download & Continue
            </Button>
          </div>
        )}

        {/* Email Form */}
        {showEmailForm && !isEditing && (
          <div className="mt-6 bg-white rounded-2xl p-6 shadow-lg">
            <h3 className="text-lg font-semibold mb-4">Send via Email</h3>
            <div className="flex gap-3">
              <Input
                type="email"
                placeholder="your@email.com"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                className="flex-1"
              />
              <Button onClick={handleSendEmail} className="bg-purple-500 text-white border-0">
                Send
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PreviewAndEdit;
